import { useContext, useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form'
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup";
import { CheckIcon, SwitchHorizontalIcon } from '@heroicons/react/solid';
import { RadioGroup } from '@headlessui/react'
import { getFunctions, httpsCallable } from "firebase/functions";
import { useIntercom } from 'react-use-intercom';

import Button from '@/components/Button';
import { ModalContext } from '../../contexts/ModalContext';
import { convertAmount } from '../../utils/currencyResolver';
import { AuthContext } from '../../contexts/AuthContext';
import { useBalance } from '@/hooks/useBalance'

export default function WithdrawPanel() {
  const [selected, setSelected] = useState("07")
  const [serverError, setServerError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { closeModal } = useContext(ModalContext)
  const { exchangeRate, userBalance } = useContext(AuthContext)
  const functions = getFunctions();
  const createOrder = httpsCallable(functions, 'createOrder');
  const { showNewMessages } = useIntercom();

  const schema = yup.object().shape({
    rsn: yup.string().required(),
    amount: yup.number().required().positive().max(userBalance, 'Not enough funds.'),
    game: yup.mixed().oneOf(['07', 'RS3'])
  });

  const { register, handleSubmit, control, getValues, setValue, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      game: "07",
      amount: 0
    }
  });

  const amount = useWatch({
    control,
    name: "amount",
    defaultValue: 0
  });

  const { displayValue, value } = useBalance(amount)

  useEffect(() => {
    setValue('game', selected)
  }, [selected])

  const onSubmitHandler = async (data) => {
    try {
      setIsLoading(true)
      const order = await createOrder({
        ...data,
        type: 'Withdraw'
      })
      if (selected == "RS3") {
        showNewMessages(`I would like to withdraw ${convertAmount(data.amount)} 07 for ${convertAmount(data.amount * exchangeRate.value)} RS3. Here is my order ${order.data}`)
      } else {
        showNewMessages(`I would like to withdraw ${convertAmount(data.amount)} 07. Here is my order ${order.data}`)
      }
      closeModal()
    } catch (error) {
      setServerError(error.message)
    }
    setIsLoading(false)
    // toast(<Toast icon={<UserIcon className="h-6 w-6 text-gray-300" />} headline="Deposit Accepted 💰" text="Good luck!" />)
  }

  const handleChange = e => {
    setValue('amount', e.target.value * 1000000)
  }

  return (
    <div className="mt-8">
      <form onSubmit={handleSubmit(onSubmitHandler)} className="grid gap-4">
        <div>
          <label className="block mb-1 font-medium text-sm">Game Mode</label>
          <RadioGroup value={selected} onChange={setSelected}>

            <div className="grid grid-cols-2 gap-4">
              <RadioGroup.Option
                key={1}
                value="07"
                className={({ checked }) =>
                  `
                  ${checked ? 'border-purple-600 opacity-100' : 'border-transparent opacity-60'
                  }
                  cursor-pointer bg-gray-900 border-2 relative rounded py-2.5 transition duration-300 px-4 flex items-center justify-center font-medium text-white`
                }
              >
                {({ checked }) => (
                  <>
                    <div className="flex items-center w-full relative">
                      <RadioGroup.Label
                        as="p"
                        className={`font-medium text-white mx-auto flex justify-center text-center`}
                      >
                        07
                      </RadioGroup.Label>
                      {checked && (
                        <div className="flex-shrink-0 text-white absolute right-0">
                          <CheckIcon className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </RadioGroup.Option>
              <RadioGroup.Option
                key={2}
                value="RS3"
                className={({ checked }) =>
                  `
                  ${checked ? 'border-purple-600 opacity-100' : 'border-transparent opacity-60'
                  }
                  cursor-pointer bg-gray-900 border-2 relative rounded py-2.5 transition duration-300 px-4 flex items-center justify-center font-medium text-white`
                }
              >
                {({ checked }) => (
                  <>
                    <div className="flex items-center w-full relative">
                      <RadioGroup.Label
                        as="p"
                        className={`font-medium text-white mx-auto flex justify-center text-center`}
                      >
                        RS3
                      </RadioGroup.Label>
                      {checked && (
                        <div className="flex-shrink-0 text-white absolute right-0">
                          <CheckIcon className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </RadioGroup.Option>
            </div>
          </RadioGroup>
        </div>
        <div>
          <label className="block mb-1 font-medium text-sm">Runescape Name</label>
          <input {...register('rsn')} type="text" required />
          {errors?.rsn && <p className="bg-red-200 p-2 border border-red-800 text-center text-red-800 rounded-md mt-2 text-xs">{errors.rsn?.message}</p>}
        </div>
        {selected == "RS3" ? (
          <>
            <div className="flex items-end">
              <div className="flex-1">
                <div className="mb-1 flex justify-between flex-wrap items-baseline">
                  <label className="block font-medium text-sm">07 <span className="text-xs">({convertAmount(amount)})</span></label>
                  <div className="flex items-baseline">
                    <span className="text-xs">Available: {convertAmount(userBalance)}</span>
                    <button onClick={() => setValue('amount', userBalance)} type="button" className="text-xs ml-1 underline text-blue-600 hover:text-blue-700">Use Max</button>
                  </div>
                </div>

                <div className="relative">
                  <input {...register('amount')} type="hidden" required className={errors?.amount ? '!border-red-500 absolute' : ' absolute'} />
                  <input type="number" step=".01" value={displayValue} onChange={handleChange} required className={errors?.amount ? '!border-red-500' : ''} />
                  <div className="absolute right-0 inset-y-0 flex p-1">
                    <div className="px-3 bg-gray-800 rounded shadow font-medium text-sm flex items-center justify-center">M</div>
                  </div>
                </div>
                {errors?.amount && <p className="text-red-500 font-medium rounded-md mt-2 text-xs">{errors.amount?.message}</p>}
              </div>
              <div className="my-auto mx-2">
                <SwitchHorizontalIcon className="h-5" />
              </div>
              <div className="flex-1">
                <label className="block mb-1 font-medium text-sm">RS3 <span className="text-xs">({convertAmount(amount)})</span></label>
                <div className="relative">
                  <input value={(displayValue * exchangeRate.value).toFixed(2)} className="bg-gray-700 border-gray-800 border-transparent shadow-md" type="number" readOnly />
                  <div className="absolute right-0 inset-y-0 flex p-1">
                    <div className="px-3 bg-gray-800 rounded shadow font-medium text-sm flex items-center justify-center">M</div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-gray-200 text-xs">
              Site balances are always stored as 07. Conversion rate 1M 07 = {exchangeRate.value}M RS3
            </p>
          </>
        ) : (
          <div>
            {/* <label className="block mb-1 font-medium text-sm">Withdraw <span className="text-xs">({convertAmount(amount)})</span></label> */}
            <div className="mb-1 flex justify-between items-baseline">
              <label className="block font-medium">Withdraw <span className="text-xs">({convertAmount(amount)})</span></label>
              <div className="flex items-baseline">
                <span className="text-xs">Available: {convertAmount(userBalance)}</span>
                <button onClick={() => setValue('amount', userBalance)} type="button" className="text-xs ml-1 underline text-blue-600 hover:text-blue-700">Use Max</button>
              </div>
            </div>
            <div className="relative">
              <input {...register('amount')} type="hidden" required className={errors?.amount ? '!border-red-500 absolute' : ' absolute'} />
              <input type="number" step=".01" value={displayValue} onChange={handleChange} required className={errors?.amount ? '!border-red-500' : ''} />
              <div className="absolute right-0 inset-y-0 flex p-1">
                <div className="px-3 bg-gray-800 rounded shadow font-medium text-sm flex items-center justify-center">M</div>
              </div>
            </div>
            {errors?.amount && <p className="text-red-500 font-medium rounded-md mt-2 text-xs">{errors.amount?.message}</p>}
          </div>
        )}
        <div>
          {serverError && <p className="text-red-500 font-medium rounded-md text-xs mb-4">{serverError}</p>}
          <Button modifier="secondary" type="submit" className="py-3.5 w-full">
            {isLoading ? (
              <span className="flex items-center justify-center">
                Processing
                <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            ) : (
              <span>Withdraw</span>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}